import { transition, trigger, useAnimation } from '@angular/animations';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Params, Router } from '@angular/router';
import {
  NgbAlertModule,
  NgbModal,
  NgbModalModule,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import {
  bounceInOnEnterAnimation,
  fadeInAnimation,
  fadeInOnEnterAnimation,
  fadeOutOnLeaveAnimation,
} from 'angular-animations';
import { DeviceDetectorService } from 'ngx-device-detector';
import {
  EMPTY,
  Observable,
  Subject,
  Subscription,
  catchError,
  finalize,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {
  DataBaseRequest,
  DataBaseResponse,
  DadosBuildService,
  DataBaseSearchService,
  ForgotPasswordService,
  LoginRequest,
  LoginSupport,
  SchoolSearchService,
  SearchSchoolRequest,
  SearchSchoolResponse,
  TokenRedirection,
  TokenRedirectionService,
  UserForm,
} from 'login-data-access';
import { NgxMaskDirective } from 'ngx-mask';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { Md5 } from 'ts-md5';
import { AuthService } from 'src/app/auth/auth.service';
import { JwtTokenService } from 'src/app/auth/jwt-token.service';
import {
  bounceIn,
  bounceOut,
} from 'src/app/animation/animations/bounce.animation';
import { Fade } from 'src/app/animation/animations/fade.animation';
import { OrigenConfigService } from 'src/app/services/origen-config.service';
import { MensagemModule } from 'src/app/shared/components/mensagem/mensagem.module';
import { ChangePasswordComponent } from '../change-password/change-password.component';
import { ForgotPasswordComponent } from '../forgot-password/forgot-password.component';
import { PasswordChangeResult } from '../password-dialog/password-dialog.validation';
import { SelectSchoolComponent } from '../select-school/select-school.component';
import {
  SupportLoginComponent,
  SupportLoginResult,
} from '../support-login/support-login.component';
import { FORMULARIO_ROUTES } from '../formulario-routes';

@Component({
  selector: 'gep-login',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MensagemModule,
    NgbModalModule,
    NgbAlertModule,
    NgxMaskDirective,
    NgOptimizedImage,
    MatProgressBarModule,
  ],
  templateUrl: './login.component.html',
  styleUrls: [
    './login.component.css',
    './login-animation.css',
    './login-footer.css',
  ],
  animations: [
    bounceInOnEnterAnimation(),
    trigger('bounce', [
      transition(
        'void => *',
        useAnimation(bounceIn, {
          params: { timing: 1 },
        }),
      ),
      transition(
        '* => void',
        useAnimation(bounceOut, {
          params: { timing: 1 },
        }),
      ),
    ]),
    Fade,
    fadeInAnimation(),
    fadeOutOnLeaveAnimation(),
    fadeInOnEnterAnimation(),
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly fb = inject(UntypedFormBuilder);
  private readonly authService = inject(AuthService);
  private readonly forgotPasswordService = inject(ForgotPasswordService);
  private readonly schoolSearchService = inject(SchoolSearchService);
  private readonly dataBaseSearchService = inject(DataBaseSearchService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly modalService = inject(NgbModal);
  private readonly deviceDetectorService = inject(DeviceDetectorService);
  private readonly dadosProdService = inject(DadosBuildService);
  private readonly tokenRedirectionService = inject(TokenRedirectionService);
  private readonly origenConfigService = inject(OrigenConfigService);
  private readonly jwtTokenService = inject(JwtTokenService);
  private readonly cdr = inject(ChangeDetectorRef);

  public form: UntypedFormGroup;
  public msgErro: string = '...';
  public msgColor: string = 'text-light';
  public msgIcon: string = '';
  public carregando: boolean = false;
  public alertErro: string = '';

  public listYear: number[] = [];

  public listaBanco: DataBaseResponse[] = [];

  public listaEscolas!: SearchSchoolResponse[];

  public isMobile: boolean = false;

  public showVersion!: boolean;
  public data = new Date().getFullYear();
  public footerTexto = this.origenConfigService.getFooterTexto(this.data);
  public footerLinkUrl = this.origenConfigService.getConfig().linkUrl;
  public exibirMascote = this.origenConfigService.getConfig().exibirMascote;
  public dataProd$ = this.dadosProdService.getDadosBuild();
  public mostrarDataProd: boolean = true;
  private intervalId?: ReturnType<typeof setInterval>;
  public loading: boolean = false;

  public localInvalido = true;

  public isTokenRedirection: boolean = false;
  public isLoadingTokenRedirection: boolean = false;
  public tokenValue: TokenRedirection[] = [];

  public modalTokenRedirectionRef!: NgbModalRef;
  public hide = false;

  private jwtTokenSubscription?: Subscription;
  private ultimoCpfComToken: string | null = null;
  private carregandoTokenJwt = false;

  // token redirection
  public tokenParams$ = this.route.queryParams.pipe(
    map((params) => this.combinarQueryParamsDaUrl(params)),
    tap((queryParams) => {
      if (this.obterQueryParam(queryParams, 'token')) {
        this.afterNextTick(() => {
          this.hide = true;
        });
      }
    }),
    switchMap((params) => {
      const token = this.obterQueryParam(params, 'token');

      if (token) {
        this.isTokenRedirection = true;
        this.isLoadingTokenRedirection = true;
        return this.gerarJwtParaTokenRedirection(params).pipe(
          switchMap(() => this.tokenRedirectionService.redirect(token)),
        );
      }
      return EMPTY;
    }),
    tap((res) => {
      this.tokenValue = res;
      this.form.get('cpf')?.setValue(res[0].CPF);
      this.form.get('local')?.setValue(res[0].BANCODADOS);
      this.form.get('senha')?.setValue(res[0].SENHA);
      this.form.get('ano')?.setValue(res[0].ANO);
      this.submit();
    }),
  );

  constructor() {
    this.form = this.fb.group({
      cpf: [null, Validators.compose([Validators.required])],
      local: [null, Validators.compose([Validators.required])],
      escola: null,
      senha: [null, Validators.required],
      ano: new Date().getFullYear(),
    });

    this.form.value.ano = '2022';
  }

  ngOnInit(): void {
    this.getYears();
    this.isMobile = this.deviceDetectorService.isMobile();

    setTimeout(() => {
      this.aoDigitarCpf();
      this.afterNextTick(() => {
        this.showVersion = true;
      });
    }, 1000);

    this.alternarTextos();
  }

  ngOnDestroy(): void {
    this.jwtTokenSubscription?.unsubscribe();
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }

  alternarTextos() {
    this.intervalId = setInterval(() => {
      this.afterNextTick(() => {
        this.mostrarDataProd = !this.mostrarDataProd;
      });
    }, 5000);
  }

  private afterNextTick(update: () => void): void {
    Promise.resolve().then(() => {
      update();
      this.cdr.detectChanges();
    });
  }

  close() {
    this.showVersion = false;
  }

  getYears(): void {
    const date: number = new Date().getFullYear();
    for (let i = 11; i >= 0; i--) {
      this.listYear.push(date - i + 1);
    }
    this.listYear.sort((a, b) => b - a);
  }

  async submit(senha?: string): Promise<void> {
    if (senha) {
      this.form.value.senha = senha;
    }
    this.authenticatePassword().subscribe((e) => {
      if (e.isTrue) {
        this.selectSchool();
        return;
      } else {
        Swal.fire({
          icon: 'error',
          title: e.aviso,
          customClass: {
            popup: 'modal-ergon',
            confirmButton: 'btn',
          },
        });
      }
    });

    if (this.form.invalid) {
      return;
    }
  }

  aoDigitarCpf(): void {
    const pegaCpf: HTMLInputElement = document.querySelector(
      '#InputCpf',
    ) as HTMLInputElement;
    const ex: RegExp = /[^0-9]/g;

    if (pegaCpf) {
      pegaCpf.addEventListener('focusout', () => {
        this.formataCpf(ex, pegaCpf);
      });

      pegaCpf.addEventListener('keyup', () => {
        const cpfNumeros = pegaCpf.value.replace(ex, '');
        const cpfValido = this.testaCpf(cpfNumeros);

        this.msgErro = cpfValido ? 'cpf válido' : 'cpf inválido';
        this.msgColor = cpfValido ? 'text-success' : 'text-danger';
        this.msgIcon = cpfValido ? 'check_circle' : 'info';

        if (cpfNumeros.length === 11 && cpfValido) {
          this.obterTokenJwt(pegaCpf.value);
        }
      });
    }
  }

  // TODO: subistituir por NgMask
  formataCpf(ex: RegExp, cpf: HTMLInputElement): void {
    const exp: RegExp = /^([\d]{3})([\d]{3})?([\d]{3})?([\d]{2})?/;

    if (cpf.value.length >= 10) {
      const value = cpf.value.replace(ex, '').replace(exp, '$1.$2.$3-$4');
      cpf.value = value;
      this.form.get('cpf')?.setValue(value);
    } else {
      const value = cpf.value.replace(ex, '');
      cpf.value = value;
    }
  }

  private obterTokenJwt(cpf: string): void {
    const cpfNumeros = cpf.replace(/\D/g, '');

    if (
      this.carregandoTokenJwt ||
      (this.ultimoCpfComToken === cpfNumeros && this.jwtTokenService.getToken())
    ) {
      if (this.jwtTokenService.getToken()) {
        void this.selectBase();
      }
      return;
    }

    this.jwtTokenSubscription?.unsubscribe();
    this.carregandoTokenJwt = true;

    this.jwtTokenSubscription = this.jwtTokenService
      .requestToken(cpf)
      .subscribe({
        next: () => {
          this.carregandoTokenJwt = false;
          this.ultimoCpfComToken = cpfNumeros;
          void this.selectBase();
        },
        error: () => {
          this.carregandoTokenJwt = false;
          this.ultimoCpfComToken = null;
          this.jwtTokenService.clearToken();
          this.listaBanco = [];
          this.form.patchValue({ local: '' });
          this.msgErro = 'CPF não cadastrado';
          this.msgColor = 'text-danger';
          this.msgIcon = 'info';
          this.cdr.detectChanges();
        },
      });
  }

  private gerarJwtParaTokenRedirection(
    params: Params,
  ): Observable<string | null> {
    const cpf = this.obterCpfTokenRedirection(params);

    if (!cpf) {
      return of(null);
    }

    this.carregandoTokenJwt = true;

    return this.jwtTokenService.requestToken(cpf).pipe(
      tap(() => {
        this.ultimoCpfComToken = cpf.replace(/\D/g, '');
      }),
      catchError(() => {
        this.ultimoCpfComToken = null;
        this.jwtTokenService.clearToken();
        this.isLoadingTokenRedirection = false;
        Swal.fire({
          icon: 'error',
          title: 'CPF não cadastrado',
          text: 'Não foi possível validar o acesso pelo token informado.',
        });
        return EMPTY;
      }),
      finalize(() => {
        this.carregandoTokenJwt = false;
      }),
    );
  }

  private combinarQueryParamsDaUrl(params: Params): Params {
    if (typeof window === 'undefined' || !window.location.search) {
      return params;
    }

    const paramsDaUrl = new URLSearchParams(window.location.search);
    const paramsExternos: Params = {};

    paramsDaUrl.forEach((value, key) => {
      if (params[key] === undefined) {
        paramsExternos[key] = value;
      }
    });

    return {
      ...paramsExternos,
      ...params,
    };
  }

  private obterCpfTokenRedirection(params: Params): string | null {
    return (
      this.obterQueryParam(params, 'CPF') ?? this.obterQueryParam(params, 'cpf')
    );
  }

  private obterQueryParam(params: Params, key: string): string | null {
    const value = params[key];
    const rawValue = Array.isArray(value) ? value[0] : value;

    if (typeof rawValue !== 'string') {
      return null;
    }

    const trimmedValue = rawValue.trim();
    return trimmedValue ? trimmedValue : null;
  }

  public testaCpf(strCPF: string) {
    let Soma;
    let Resto;
    Soma = 0;
    if (strCPF == '00000000000') return false;

    for (let i = 1; i <= 9; i++)
      Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (11 - i);
    Resto = (Soma * 10) % 11;

    if (Resto == 10 || Resto == 11) Resto = 0;
    if (Resto != parseInt(strCPF.substring(9, 10))) return false;

    Soma = 0;
    for (let i = 1; i <= 10; i++)
      Soma = Soma + parseInt(strCPF.substring(i - 1, i)) * (12 - i);
    Resto = (Soma * 10) % 11;

    if (Resto == 10 || Resto == 11) Resto = 0;
    if (Resto != parseInt(strCPF.substring(10, 11))) return false;
    return true;
  }

  async selectSchool() {
    const cpf: string = this.form.value.cpf;
    const local: string = this.retornaBanco();

    if (cpf === null && local === null) {
      Swal.fire('Digite o CPF');
      return;
    }

    try {
      const valorEmitir: SearchSchoolRequest = { cpf: cpf, local: local };
      const toSearch: SearchSchoolResponse[] =
        await this.schoolSearchService.searchSchool(valorEmitir);

      const newToSearch: SearchSchoolResponse[] = [];

      const modal: Record<number, string> = {};
      let cont = 1;
      toSearch.forEach((e) => {
        modal[cont] = `${e.DESCRICAO_GRUPO} - ${e.ESCOLA} - ${e.CDESCOLA}`;

        newToSearch.push({
          VALUE: cont,
          ...e,
        });

        cont++;
      });

      this.listaEscolas = newToSearch;
      let indexEscola;

      if (toSearch.length > 1) {
        this.openSelectSchool(newToSearch);
      } else if (toSearch.length == 1) {
        if (this.isTokenRedirection) {
          this.autoSelectSchool();

          return;
        }

        const schoolSelected = toSearch[0];

        this.login(
          {
            CDESCOLA: schoolSelected.CDESCOLA,
            DESCRICAO_GRUPO: schoolSelected.DESCRICAO_GRUPO,
          },
          schoolSelected,
          toSearch,
        );
      } else {
        indexEscola = await Swal.fire({
          title: 'Selecione a escola',
          input: 'select',
          inputOptions: modal,
          showCancelButton: true,
          confirmButtonColor: '#AB58B9',
          inputValidator: (value) => {
            return new Promise((resolve: (msg?: string) => void) => {
              if (value != '' || value != null) {
                resolve();
              } else {
                resolve('Você precisa selecionar uma escola');
              }
            });
          },
        });
      }

      if (indexEscola) {
        this.carregando = true;
      }
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.log(error);
    }
  }

  private openSelectSchool(schools: SearchSchoolResponse[]): void {
    if (this.isTokenRedirection) {
      this.autoSelectSchool();
      return;
    }

    const modalRef = this.modalService.open(SelectSchoolComponent, {
      ariaLabelledBy: 'modal-basic-title',
      scrollable: true,
      ...(this.isMobile
        ? {
            modalDialogClass: 'modal-fullscreen',
            windowClass: 'modal-select-school-mobile',
          }
        : { size: 'xl' as const }),
    });
    modalRef.componentInstance.configure(schools, this.isMobile);

    modalRef.result.then(
      (school: SearchSchoolResponse) => {
        this.loading = true;
        void this.login(
          {
            CDESCOLA: Number(school.CDESCOLA),
            DESCRICAO_GRUPO: school.DESCRICAO_GRUPO,
          },
          school,
          schools,
        );
      },
      () => undefined,
    );
  }

  autoSelectSchool(profundidade: number = 0, limite: number = 2) {
    let escolaSelected;

    for (let i = 0; i < this.listaEscolas.length; i++) {
      const element = this.listaEscolas[i];

      if (
        element.CDESCOLA == this.tokenValue[0].CDESCOLA &&
        // trocado comparação de GRUPO_USUARIO para PERFIL_ACESSO para corrigir erro de comparação 13/02/2025
        element.PERFILACESSO == this.tokenValue[0].PERFIL_ACESSO
      ) {
        escolaSelected = element;
      }
    }

    if (!escolaSelected && this.tokenValue.length > 0) {
      this.schoolSearchService
        .getSchools({
          cpf: this.tokenValue[0].CPF,
          local: this.tokenValue[0].BANCODADOS,
          cdescola: this.tokenValue[0].CDESCOLA,
          perfilacesso: this.tokenValue[0].PERFIL_ACESSO,
        })
        .subscribe((res) => {
          this.listaEscolas = res;
          // Recursividade para revalidar a seleção após a nova busca
          this.autoSelectSchool(profundidade + 1, limite);
        });
    }

    if (escolaSelected) {
      const res = {
        CDESCOLA: this.tokenValue[0].CDESCOLA,
        DESCRICAO_GRUPO: escolaSelected!.DESCRICAO_GRUPO,
      };

      this.login(res, escolaSelected, this.listaEscolas);
      this.isLoadingTokenRedirection = false;
    }
  }

  public async login(
    res: { CDESCOLA: number; DESCRICAO_GRUPO: string },
    escolaSelected: SearchSchoolResponse | undefined,
    newToSearch: SearchSchoolResponse[],
  ) {
    try {
      const token =
        Math.random().toString(16).substring(2) +
        Math.random().toString(16).substring(2);

      await this.authService.login({
        ...this.form.value,
        local: this.retornaBanco(),
        cdescola: res.CDESCOLA,
        token: token,
        perfilacesso: escolaSelected?.PERFILACESSO,
        escola: escolaSelected,
        ID_GRUPO: escolaSelected?.ID_GRUPO,
        aviso: 'Acesso pelo login',
      });
      localStorage.setItem('schoolsList', JSON.stringify(newToSearch));
      localStorage.setItem('schoolLogged', JSON.stringify(escolaSelected));
      if (!this.isTokenRedirection) {
        this.router.navigateByUrl('/painel');
      } else {
        const rotasToken = FORMULARIO_ROUTES;

        if (rotasToken[this.tokenValue[0].FORMULARIO]) {
          this.router.navigateByUrl(rotasToken[this.tokenValue[0].FORMULARIO]);
        }
      }
    } catch (error: unknown) {
      this.loading = false;

      if (error instanceof HttpErrorResponse) {
        return;
      }

      Swal.fire({
        icon: 'error',
        title: error instanceof Error ? error.message : 'Erro ao fazer login',
      });
    }

    if (this.isTokenRedirection) {
      if (this.modalTokenRedirectionRef) {
        this.modalTokenRedirectionRef.close();
      }
    }
  }

  async selectBase() {
    try {
      const cpf: string = this.form.value.cpf;
      const valorEmitir: DataBaseRequest = { cpf: cpf };
      const resposta =
        await this.dataBaseSearchService.dataBaseListRequest(valorEmitir);

      if (
        resposta &&
        typeof resposta === 'object' &&
        !Array.isArray(resposta) &&
        'error' in resposta
      ) {
        throw new Error(String((resposta as { error: unknown }).error));
      }

      if (resposta.length == 0) {
        throw new Error('Banco de dados não localizado!');
      }

      this.listaBanco = resposta;
      if (resposta.length == 1) {
        this.form.patchValue({ local: [resposta[0].BANCODADOS] });
        document.querySelector('#local')?.setAttribute('disabled', 'true');
      } else {
        document.querySelector('#local')?.removeAttribute('disabled');
        this.form.patchValue({ local: '' });
      }
      this.cdr.detectChanges();
    } catch (error: unknown) {
      const httpError = error instanceof HttpErrorResponse ? error : undefined;
      Swal.fire({
        icon: 'error',
        title: 'Banco de dados não localizado!',
        text: httpError?.error?.title ?? 'Banco de dados não localizado!',
        customClass: {
          confirmButton: 'btn',
          popup: 'modal-ergon',
        },
      }).then(() => location.reload());
    }
  }

  esqueceuSenha(): void {
    const dadosDigitados: UserForm = this.form.value;
    if (
      dadosDigitados.cpf === null ||
      dadosDigitados.local === null ||
      dadosDigitados.cpf === '' ||
      dadosDigitados.local === ''
    ) {
      Swal.fire({
        icon: 'info',
        title: 'Dados incompletos',
        text: 'Digite o CPF e selecione o local para recuperar a senha.',
        confirmButtonText: 'Entendi',
        customClass: {
          confirmButton: 'btn btn-ergon btn-shadow',
          popup: 'modal-ergon',
        },
        confirmButtonColor: '#AB58B9',
      });
      return;
    }

    const modalRef = this.modalService.open(ForgotPasswordComponent, {
      ariaLabelledBy: 'forgot-password-title',
      centered: true,
    });

    modalRef.result.then(
      (email: string) => {
        void this.enviarRecuperacaoSenha(dadosDigitados.cpf!, email);
      },
      () => undefined,
    );
  }

  private async enviarRecuperacaoSenha(
    cpf: string,
    email: string,
  ): Promise<void> {
    try {
      const valorEmitir = {
        cpf,
        email,
        local: this.retornaBanco(),
      } as LoginRequest;
      const forgot =
        await this.forgotPasswordService.forgotPassword(valorEmitir);

      Swal.fire({
        icon: 'success',
        title: forgot.aviso,
        confirmButtonText: 'Entendi',
        customClass: {
          confirmButton: 'btn btn-ergon btn-shadow',
          popup: 'modal-ergon',
        },
        confirmButtonColor: '#6F31AD',
      });
    } catch (error: unknown) {
      Swal.fire({
        icon: 'error',
        title:
          error instanceof Error
            ? error.message
            : 'Erro ao recuperar senha. Tente novamente.',
        confirmButtonText: 'Entendi',
        customClass: {
          confirmButton: 'btn btn-ergon btn-shadow',
          popup: 'modal-ergon',
        },
        confirmButtonColor: '#6F31AD',
      });
    }
  }

  alterarSenha(): void {
    if (this.form.get('cpf')?.invalid) {
      Swal.fire({
        title: 'Informe o CPF',
        text: 'Digite seu CPF no formulário de login para alterar a senha.',
        icon: 'info',
        confirmButtonText: 'Entendi',
        customClass: {
          confirmButton: 'btn btn-ergon btn-shadow',
          popup: 'modal-ergon',
        },
        confirmButtonColor: '#AB58B9',
      });
      return;
    }

    const modalRef = this.modalService.open(ChangePasswordComponent, {
      ariaLabelledBy: 'change-password-title',
      centered: true,
    });

    modalRef.result.then(
      (senhas: PasswordChangeResult) => {
        this.changePassword(
          this.form.value.cpf,
          this.retornaBanco(),
          senhas.novaSenha,
          senhas.senhaAtual,
        );
      },
      () => undefined,
    );
  }

  public changePassword(
    cpf: string,
    local: string,
    newPass: string,
    oldPass: string,
  ) {
    this.authService.changePassword(cpf, local, newPass, oldPass).subscribe({
      next: (item) => {
        Swal.fire({
          icon: 'success',
          title: item.aviso,
        });
      },
    });
  }

  public authenticatePassword(): Observable<{
    isTrue: boolean;
    aviso: string;
  }> {
    //md5.appendStr(senha.toLocaleUpperCase()).end() as string
    let result: boolean;
    const subject = new Subject<{ isTrue: boolean; aviso: string }>();
    //this.form.value.ano = '2022';
    const md5 = new Md5();
    const senha: string = this.form.value.senha;
    const banco = this.retornaBanco();

    let url: string | undefined;
    if (this.isTokenRedirection) {
      url = this.tokenValue[0].URL_BASE;
    } else {
      url =
        typeof window !== 'undefined' ? window.location.hostname : undefined;
    }

    this.authService
      .validaSenha(
        md5.appendStr(senha.toLocaleUpperCase()).end() as string,
        banco,
        this.form.value.cpf,
        url,
      )
      .subscribe({
        next: (e) => {
          switch (e.aviso) {
            case 'OK':
              result = true;
              subject.next({ isTrue: result, aviso: e.aviso });
              break;
            case 'SENHA INVÁLIDA':
              result = false;
              subject.next({ isTrue: result, aviso: e.aviso });
              break;
            default:
              break;
          }
        },
        error: (error) => {
          result = false;
          subject.next({ isTrue: result, aviso: error.error.title });
        },
      });

    return subject.asObservable();
  }
  retornaBanco(): string {
    const banco = this.listaBanco.find(
      (element) => element.CLIENTE == this.form.value.local,
    );
    if (this.listaBanco.length == 1) {
      return this.listaBanco[0].BANCODADOS;
    } else if (this.isTokenRedirection) {
      return this.form.value.local;
    } else {
      return banco!.BANCODADOS;
    }
  }

  public logarComSuporte(): void {
    const modalRef = this.modalService.open(SupportLoginComponent, {
      ariaLabelledBy: 'login-suporte-title',
      centered: true,
    });
    modalRef.componentInstance.authenticate = (nome: string, senha: string) =>
      this.autenticarSuporte(nome, senha);
  }

  private autenticarSuporte(
    nome: string,
    senha: string,
  ): Promise<SupportLoginResult> {
    const md5 = new Md5();

    return new Promise((resolve) => {
      this.authService
        .getLoginSupport({
          CPF: this.form.value.cpf,
          DATABASE: this.retornaBanco(),
          NOME: nome,
          SENHA: md5.appendStr(senha.toLocaleUpperCase()).end() as string,
        })
        .subscribe({
          next: (dadosLogin) => {
            const loginComAviso = dadosLogin as
              LoginSupport[] | { aviso: string };

            if (!Array.isArray(loginComAviso) && loginComAviso.aviso) {
              Swal.fire({
                icon: 'error',
                title: 'Ops!',
                text: loginComAviso.aviso,
              });
              resolve({ ok: false, feedbackShown: true });
              return;
            }

            const listaLogin = dadosLogin as LoginSupport[];

            if (
              listaLogin[0]?.SENHA &&
              listaLogin[0]?.NOME &&
              listaLogin[0]?.CPF
            ) {
              this.submit(listaLogin[0].SENHA);
              resolve({ ok: true });
              return;
            }

            resolve({ ok: false, message: 'Dados de login inválidos' });
          },
          error: (err: unknown) => {
            resolve({
              ok: false,
              message:
                err instanceof Error ? err.message : 'Dados de login inválidos',
            });
          },
        });
    });
  }

  validarLocal() {
    const index = this.listaBanco.findIndex(
      (element) => element.CLIENTE == this.form.value.local,
    );

    if (index == -1) {
      this.localInvalido = true;
      Swal.fire({
        icon: 'error',
        title: 'Atenção',
        text: 'Você digitou um local inválido, tente novamente',
      });
    } else {
      this.localInvalido = false;
    }
  }
}
